/**
 * The vertical rhythm of a care-plan card, as one scale rather than a class per call site.
 *
 * The steps carry the hierarchy, so what matters is the ratio between them, not any one value. A
 * heading has to read as belonging to the list under it rather than to the section above, which only
 * holds while the space above it is clearly greater than the space below:
 *
 * | between | step |
 * | --- | --- |
 * | two items in a section | none — the rows' own `py-0.5` is the only space |
 * | a heading and its items | 8px (`CARE_PLAN_HEADING_GAP`) |
 * | one section and the next | 20px (`CARE_PLAN_SECTION_GAP`) |
 * | one section *group* and the next | 12px either side of a `Separator` |
 *
 * A card lays its own children out on 12px, which is the group step — so anything that is a card child
 * rather than a section (Thoughts) takes `CARE_PLAN_THOUGHTS_STEP` on top to reach the section step.
 *
 * The detail overlay is deliberately not on this scale: its rows are full-size charts rather than lines,
 * and it spaces them at `gap-3` throughout.
 */

/** A section heading, above its own items. Twice the space between the items themselves. */
export const CARE_PLAN_HEADING_GAP = 'gap-2';

/**
 * One section above the next, where no rule divides them — Medications above Lifestyle, which share a
 * group. Two and a half times the heading gap, so a heading cannot read as belonging to the section
 * above it.
 */
export const CARE_PLAN_SECTION_GAP = 'gap-5';

/**
 * What Thoughts adds to the card's own 12px to reach `CARE_PLAN_SECTION_GAP`. Not needed when a rule
 * sits above it: the rule's own spacing already matches the group boundaries.
 */
export const CARE_PLAN_THOUGHTS_STEP = 'pt-2';
